import {
  useEffect, useState, useCallback, useRef
} from 'react';
import queryString from 'query-string';
import { promoCodeAPI, welcomePromoCodesAPI } from '@common/api/api';
import {
  hideBodyScroll, unlockBodyScroll, toCamelCase, withCaptcha
} from '../../../../common/config/utils';
import { parseAndTrackAnalyticEvents } from '../../../actions/analytics';
import { getCookie, removeCookie, setCookie } from '../../../config/cookies_action';
import { log, LOG_LEVELS } from '../../../../common/config/app_logger';

interface BonusPopup {
  title?: string;
  description: string;
}

interface SocialParams {
  [key: string]: any;
}

interface UseAutoRedeemPromoCodeProps {
  setApplicationError: (error: { errorCode?: number; statusText?: string }) => void;
  clearPromoCode: () => void;
  userId?: number;
}

interface UseAutoRedeemPromoCodeReturn {
  loading: boolean;
  bonusPopup: BonusPopup | null;
  clearBonusPopup: () => void;
}

const getAutoRedeemPromoCode = (): string | null => {
  const promo = toCamelCase(queryString.parse(window.location.search))?.promo
    || getCookie({ cookieName: 'autoRedeemPromoCode', cookieOptions: {} });
  return promo || null;
};

const clearPromoCodeCookie = (): void => {
  if (getCookie({ cookieName: 'autoRedeemPromoCode', cookieOptions: {} })) {
    removeCookie({ cookieName: 'autoRedeemPromoCode', cookieOptions: { path: '/' } });
  }
};

const removePromoFromUrl = (): void => {
  const search = queryString.parse(window.location.search);
  if (search.promo) {
    delete search.promo;
    const updatedSearch = queryString.stringify(search);
    const url = `${ window.location.pathname }${ updatedSearch ? `?${ updatedSearch }` : '' }`;
    window.history.replaceState({ ...window.history.state }, '', url);
  }
};

export const useAutoRedeemPromoCode = ({
  setApplicationError,
  clearPromoCode,
  userId
}: UseAutoRedeemPromoCodeProps): UseAutoRedeemPromoCodeReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [bonusPopup, setBonusPopup] = useState<BonusPopup | null>(null);
  const hasProcessedRef = useRef<boolean>(false);

  const promoCodeProcessingComplete = useCallback((isJustSignedUp: boolean, socialParams: SocialParams): void => {
    window.dispatchEvent(new CustomEvent('promoCodeProcessingComplete', {
      detail: { isJustSignedUp, socialParams }
    }));
  }, []);

  const processPromoCode = useCallback(async (
    isJustSignedUp: boolean,
    socialParams: SocialParams,
    promoCodeParam?: string | null
  ): Promise<void> => {
    try {
      const promoCode = promoCodeParam ?? getAutoRedeemPromoCode();

      if (!isJustSignedUp && !promoCode) {
        promoCodeProcessingComplete(isJustSignedUp, socialParams);
        return;
      }

      if (hasProcessedRef.current) return;
      hasProcessedRef.current = true;

      setLoading(true);
      hideBodyScroll();

      let deeplink: string | null = null;
      if (promoCode) {
        deeplink = `promo/${ promoCode }`;
        clearPromoCode();
        removePromoFromUrl();
        clearPromoCodeCookie();
      }

      let res: any;
      if (isJustSignedUp) {
        res = await new Promise((resolve, reject) => {
          withCaptcha('promo', (token: string) => {
            welcomePromoCodesAPI(deeplink, token).then(resolve).catch(reject);
          });
        });
      } else {
        res = await new Promise((resolve, reject) => {
          withCaptcha('promo', (token: string) => {
            promoCodeAPI(promoCode!, token).then(resolve).catch(reject);
          });
        });
      }
      if (res.bonusPopup) {
        setBonusPopup(res.bonusPopup);
      }

      setLoading(false);
      unlockBodyScroll();
      promoCodeProcessingComplete(isJustSignedUp, socialParams);
    } catch (error: any) {
      log(LOG_LEVELS.ERROR, 'AutoRedeemPromoCode', 'Promo code processing error', error);
      setLoading(false);
      unlockBodyScroll();
      try {
        const jsonError = await error.json();
        const jsonErrorCamelCase = toCamelCase(jsonError);
        if (jsonErrorCamelCase.analyticEvents) {
          parseAndTrackAnalyticEvents(jsonErrorCamelCase.analyticEvents);
        }
        if (jsonErrorCamelCase.fullMessages?.[0]) {
          setBonusPopup({ description: jsonErrorCamelCase.fullMessages[0] });
        }
      } catch {
        setApplicationError({
          errorCode: error.status,
          statusText: error.statusText
        });
      }
      promoCodeProcessingComplete(isJustSignedUp, socialParams);
    }
  }, [clearPromoCode, setApplicationError, promoCodeProcessingComplete]);

  // Save promo to cookie on mount (for auth flow - preserves promo during login)
  useEffect(() => {
    const { promo } = toCamelCase(queryString.parse(window.location.search));
    if (promo) {
      const date = new Date();
      date.setTime(date.getTime() + (30 * 60 * 1000));
      setCookie({
        cookieName: 'autoRedeemPromoCode',
        cookieValue: promo,
        cookieOptions: { path: '/', expires: date }
      });
    }
  }, []);

  // Process for already logged-in users (runs when userId becomes available)
  useEffect(() => {
    if (userId && !hasProcessedRef.current) {
      const promoCode = getAutoRedeemPromoCode();
      if (promoCode) {
        processPromoCode(false, {}, promoCode);
      }
    }
  }, [userId, processPromoCode]);

  // Handle promo for users who authenticate during this session
  useEffect(() => {
    const handleUserRegistered = (event: CustomEvent): void => {
      if (hasProcessedRef.current) return;
      processPromoCode(true, event.detail);
    };
    const handleUserLoggedIn = (event: CustomEvent): void => {
      if (hasProcessedRef.current) return;
      const promoCode = getAutoRedeemPromoCode();
      if (!promoCode) return;
      processPromoCode(false, event.detail, promoCode);
    };
    window.addEventListener('userRegistered', handleUserRegistered as EventListener);
    window.addEventListener('userLoggedIn', handleUserLoggedIn as EventListener);

    return () => {
      window.removeEventListener('userRegistered', handleUserRegistered as EventListener);
      window.removeEventListener('userLoggedIn', handleUserLoggedIn as EventListener);
    };
  }, [processPromoCode]);

  const clearBonusPopup = useCallback((): void => {
    setBonusPopup(null);
  }, []);

  return {
    loading,
    bonusPopup,
    clearBonusPopup
  };
};
